import { BrowserQRCodeReader } from "@zxing/browser";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

const MAX_PDF_PAGES = 3;
const ROTATIONS = [0, 90, 180, 270];

export const isKaspiReceiptUrl = (value) => {
  try {
    const u = new URL(value);
    return (
      u.protocol === "https:" &&
      u.hostname === "receipt.kaspi.kz" &&
      (u.pathname === "/web" || u.pathname.startsWith("/web/"))
    );
  } catch {
    return false;
  }
};

const rotateCanvas = (source, deg) => {
  const w = source.width, h = source.height;
  const canvas = document.createElement("canvas");
  const swap = deg === 90 || deg === 270;
  canvas.width = swap ? h : w;
  canvas.height = swap ? w : h;
  const ctx = canvas.getContext("2d");
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((deg * Math.PI) / 180);
  ctx.drawImage(source, -w / 2, -h / 2);
  return canvas;
};

const decodeCanvas = async (canvas) => {
  if ("BarcodeDetector" in window) {
    try {
      const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
      const codes = await detector.detect(canvas);
      const hit = codes.map((c) => c.rawValue).find(isKaspiReceiptUrl);
      if (hit) return hit;
    } catch {
      /* fall through to ZXing */
    }
  }
  try {
    const reader = new BrowserQRCodeReader();
    const result = await reader.decodeFromCanvas(canvas);
    const text = result?.getText?.() || result?.text;
    if (text && isKaspiReceiptUrl(text)) return text;
  } catch {
    /* not found on this canvas */
  }
  return null;
};

const scanWithRotations = async (canvas) => {
  for (const deg of ROTATIONS) {
    const target = deg === 0 ? canvas : rotateCanvas(canvas, deg);
    const found = await decodeCanvas(target);
    if (found) return found;
  }
  return null;
};

const imageToCanvas = async (file) => {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    if (img.decode) await img.decode();
    else await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
    const scale = Math.min(2, Math.max(1, 1400 / Math.max(img.width, img.height)));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas;
  } finally {
    URL.revokeObjectURL(url);
  }
};

/** Returns the Kaspi receipt URL found in the file, or null. Never throws on decode failures. */
export const extractReceiptUrl = async (file) => {
  try {
    if (file.type === "application/pdf") {
      const pdf = await pdfjsLib.getDocument({ data: await file.arrayBuffer() }).promise;
      const pages = Math.min(pdf.numPages, MAX_PDF_PAGES);
      for (let i = 1; i <= pages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 3 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        const found = await scanWithRotations(canvas);
        if (found) return found;
      }
      return null;
    }
    if (file.type.startsWith("image/")) {
      return await scanWithRotations(await imageToCanvas(file));
    }
  } catch (e) {
    console.error("QR extraction failed", e);
  }
  return null;
};

export const sha256File = async (file) => {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
};
